'use strict';

import { gsap } from 'gsap';

import {
    clampScrollAfterLayoutShrink,
    resumeLayoutScrollEffects,
    suspendLayoutScrollEffects,
} from '../../shared/layout-scroll-effects.js';

const RECIPIENT_FIELDS_CLASS = 'vine-stock-order-form__recipient-fields';
const RECIPIENT_FIELDS_SELECTOR = `.${RECIPIENT_FIELDS_CLASS}`;
const SELF_ORDERING_SELECTOR = 'input[type="checkbox"][name*="selfOrdering"]';
const COLLAPSED_CLASS = 'is-recipient-collapsed';
const ANIMATING_CLASS = 'is-animating';

const SHOW_DURATION = 0.4;
const HIDE_DURATION = 0.4;
const SHOW_EASE = 'power2.out';
const HIDE_EASE = 'power2.in';
const COLLAPSED_OFFSET_Y = -8;

/**
 * @returns {boolean}
 */
function prefersReducedMotion() {
    return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

/**
 * @param {HTMLInputElement} checkbox
 * @returns {HTMLElement | null}
 */
function findRecipientFieldsContainer(checkbox) {
    const scopedForm = checkbox.closest('form');
    const byClass = scopedForm?.querySelector(RECIPIENT_FIELDS_SELECTOR);
    if (byClass instanceof HTMLElement) {
        return byClass;
    }

    const checkboxField = checkbox.closest('.clearfix');
    let sibling = checkboxField?.nextElementSibling ?? null;

    while (sibling) {
        if (sibling instanceof HTMLFieldSetElement) {
            sibling.classList.add(RECIPIENT_FIELDS_CLASS);
            return sibling;
        }
        sibling = sibling.nextElementSibling;
    }

    return null;
}

/**
 * @param {Element} root
 * @param {HTMLFormElement} form
 * @returns {Element}
 */
function resolveToggleRoot(root, form) {
    if (root instanceof HTMLElement) {
        return root;
    }

    return (
        form.closest('[data-vine-stock-order-form]') ||
        form.closest('.vine-stock-order-form') ||
        form.closest('.jf-site-content-vinestockorderform') ||
        form
    );
}

/**
 * @param {Element} container
 * @param {boolean} hidden
 */
function setAccessibilityState(container, hidden) {
    container.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    container.classList.toggle('is-collapsed', hidden);
}

/**
 * @param {Element} container
 */
function resetContainerStyles(container) {
    gsap.killTweensOf(container);
    gsap.set(container, { clearProps: 'all' });
    container.classList.remove(ANIMATING_CLASS);
}

/**
 * @param {Element} root
 * @param {Element} container
 */
function collapseInstant(root, container) {
    resetContainerStyles(container);
    root.classList.add(COLLAPSED_CLASS);
    setAccessibilityState(container, true);
}

/**
 * @param {Element} root
 * @param {Element} container
 */
function expandInstant(root, container) {
    resetContainerStyles(container);
    root.classList.remove(COLLAPSED_CLASS);
    setAccessibilityState(container, false);
}

/**
 * @param {Element} root
 * @param {Element} container
 * @returns {Promise<void>}
 */
function animateShow(root, container) {
    return new Promise((resolve) => {
        suspendLayoutScrollEffects();
        resetContainerStyles(container);
        root.classList.remove(COLLAPSED_CLASS);
        container.classList.add(ANIMATING_CLASS);
        setAccessibilityState(container, false);

        gsap.fromTo(
            container,
            { height: 0, opacity: 0, overflow: 'hidden', y: COLLAPSED_OFFSET_Y },
            {
                height: 'auto',
                opacity: 1,
                y: 0,
                duration: SHOW_DURATION,
                ease: SHOW_EASE,
                onComplete: () => {
                    gsap.set(container, { overflow: 'visible', clearProps: 'height,y' });
                    container.classList.remove(ANIMATING_CLASS);
                    resumeLayoutScrollEffects();
                    resolve();
                },
            }
        );
    });
}

/**
 * @param {Element} root
 * @param {Element} container
 * @returns {Promise<void>}
 */
function animateHide(root, container) {
    return new Promise((resolve) => {
        suspendLayoutScrollEffects();
        const scrollBefore = window.scrollY;

        root.classList.remove(COLLAPSED_CLASS);
        container.classList.add(ANIMATING_CLASS);
        setAccessibilityState(container, false);

        const startHeight = container.offsetHeight;

        gsap.fromTo(
            container,
            { height: startHeight, opacity: 1, y: 0, overflow: 'hidden' },
            {
                height: 0,
                opacity: 0,
                y: COLLAPSED_OFFSET_Y,
                duration: HIDE_DURATION,
                ease: HIDE_EASE,
                onComplete: () => {
                    resetContainerStyles(container);
                    root.classList.add(COLLAPSED_CLASS);
                    setAccessibilityState(container, true);
                    container.classList.remove(ANIMATING_CLASS);

                    requestAnimationFrame(() => {
                        clampScrollAfterLayoutShrink(scrollBefore);
                        resumeLayoutScrollEffects();
                        resolve();
                    });
                },
            }
        );
    });
}

/**
 * Affiche ou masque les champs propriétaire selon la case « Je commande pour moi-même ».
 *
 * @param {Element | null} root
 * @param {HTMLFormElement} form
 */
export function initRecipientToggle(root, form) {
    const checkbox = form.querySelector(SELF_ORDERING_SELECTOR);
    const container = checkbox instanceof HTMLInputElement
        ? findRecipientFieldsContainer(checkbox)
        : null;

    if (!(checkbox instanceof HTMLInputElement) || !container) {
        return;
    }

    const toggleRoot = resolveToggleRoot(root, form);
    const reducedMotion = prefersReducedMotion();
    let isAnimating = false;

    const applyState = async (animate) => {
        const isSelfOrdering = checkbox.checked;

        if (!animate || reducedMotion) {
            if (isSelfOrdering) {
                collapseInstant(toggleRoot, container);
            } else {
                expandInstant(toggleRoot, container);
            }
            return;
        }

        if (isAnimating) {
            resetContainerStyles(container);
        }

        isAnimating = true;

        if (isSelfOrdering) {
            await animateHide(toggleRoot, container);
        } else {
            await animateShow(toggleRoot, container);
        }

        isAnimating = false;
    };

    applyState(false);
    toggleRoot.setAttribute('data-form-initialized', 'true');

    checkbox.addEventListener('change', () => {
        applyState(true);
    });
}
